<template>
	<div
		class="hdl-matrix question"
		:aria-labelledby="`${question.uuid}-label`"
		:id="`${question.uuid}-container`"
		:class="{
			'has-error': errorMap.size > 0
		}"
		@keydown="keydownHandler"
		v-show="question.visible"
		ref="container"
	>
		<fieldset>
			<legend>
				<div
					ref="rendered"
					v-html="question.renderedQuestion"
				/>
			</legend>

			<div class="errors" :id="`errs-${question.uuid}`" v-if="errorMessages.length" aria-live="assertive">
				<ul>
					<li v-for="errorMessage of errorMessages" :key="errorMessage">
						{{ errorMessage }}
					</li>
				</ul>
			</div>

			<div class="matrix-container hdl-scroll overflow-x-auto overflow-y-hidden">
				<table
					ref="table"
				>
					<colgroup>
						<col class="first-column" :style="firstColumnStyle" />
						<col class="sub-columns" :style="subColumnStyle" :span="columns.length" />
					</colgroup>
					<thead>
						<tr>
							<th>&nbsp;</th>
							<th
								v-for="col, index in columns"
								:key="col.uuid"
								class="sub-column-header"
							>
								<div :id="`sq-${col.uuid}`">{{ col.label }}</div>
							</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="row, rowIndex of rows"
							:key="row.uuid"
							:role="(!isMultiSelect)? 'radiogroup' : ''"
							:aria-labelledby="`${question.uuid}-label`"
							:aria-describedby="`se-${row.uuid}`"
							:aria-required="(question.required)? 'true' : 'false'"
						>
							<td><div :id="`se-${row.uuid}`">{{ row.label }}</div></td>
							<td
								v-for="col, colIndex in columns"
								:key="col.uuid"
							>
								<div
									:role="(isMultiSelect)? 'checkbox' : 'radio'"
									class="mq-cell"
									@click.prevent.stop="selectEntry(row.uuid, col.uuid)"
									:data-row-uuid="row.uuid"
									:data-col-uuid="col.uuid"
									:data-index="(rowIndex*columns.length) + colIndex"
								>
									<radio-indicator
										:selected="isSelected(row.uuid, col.uuid)"
										:with-native="{
											id: `${row.uuid}-${col.uuid}`, name: row.uuid, labelledBy: `se-${row.uuid} sq-${col.uuid}`
										}"
										@radio:change="selectEntry(row.uuid, col.uuid)"
										v-if="!isMultiSelect"
									/>
									<checkbox-indicator
										:selected="isSelected(row.uuid, col.uuid)"
										:with-native="{
											id: `${row.uuid}-${col.uuid}`, name: row.uuid, labelledBy: `se-${row.uuid} sq-${col.uuid}`
										}"
										@checkbox:change="selectEntry(row.uuid, col.uuid)"
										v-else
									/>
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</fieldset>
	</div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';
import { IQuestionComponent } from './types';
import { PublishedSubQuestionEntryType, PublishedSubQuestionType, PublishedSurveyQuestionType, QuestionOptionKey, SubResponseType, SurveyResponseErrorCode, SurveyResponseInputErrorType } from '@/common/interfaces';
import { useResponseStore } from '@/respondants/stores/responseStore';
import { ResponseGroup } from '@/respondants/responses';
import { inPlaceShuffleArray } from '@/common/utils';

@Component({
	emits: ['intentionalBlur']
})
export default class Matrix extends Vue implements IQuestionComponent {
	@Prop({ required: true })
	question: PublishedSurveyQuestionType;

	errorMap: Map<SurveyResponseErrorCode, string> = new Map();

	response: Array<SubResponseType> | null = null;

	mounted(): void {
		this.$el.__vue__ = this;

		this.errorMap = new Map();

		this.response = null;
		if (this.responseGroup) {
			const existingResponse = this.responseGroup.getResponse(this.question.uuid);
			if (existingResponse && existingResponse.subResponses && existingResponse.subResponses.length) {
				this.response = [...existingResponse.subResponses];
			}
		}

		this.accessorizeRendered();
	}

	focusFrom(fromDirection: 'above' | 'below'): boolean {
		if (fromDirection === 'above') {
			this.focusRow(0);
		} else {
			this.focusRow(this.rows.length - 1);
		}

		return true;
	}

	keydownHandler(event: KeyboardEvent): void {
		const focusedInfo = this.focusedInfo();

		if (focusedInfo === null) {
			return;
		}

		const { rowUuid, colUuid, index } = focusedInfo;

		if (event.key === 'Enter') {
			event.stopPropagation();
			event.preventDefault();

			this.selectEntry(rowUuid, colUuid);
		}

		if (this.isMultiSelect) {
			if (event.key === 'ArrowDown') {
				event.stopPropagation();
				event.preventDefault();
				this.moveFocus('down');
			} else if (event.key === 'ArrowUp') {
				event.stopPropagation();
				event.preventDefault();
				this.moveFocus('up');
			} else if (event.key === 'ArrowLeft') {
				event.stopPropagation();
				event.preventDefault();
				this.moveFocus('left');
			} else if (event.key === 'ArrowRight') {
				event.stopPropagation();
				event.preventDefault();
				this.moveFocus('right');
			}
		}
	}

	accessorizeRendered(): void {
		const rendered = this.$refs.rendered as HTMLDivElement | undefined;

		if (rendered === undefined) {
			return;
		}

		const label = rendered.querySelector(`label[for="${this.question.uuid}"]`);

		if (label !== null) {
			const labelTail = document.createElement('div');
			labelTail.classList.add('tail');
			label.appendChild(labelTail);
			label.classList.add('question-label');

			if (this.question.required) {
				const sroRequiredIndicator = document.createElement('span');
				sroRequiredIndicator.classList.add('hdl-sr-only');
				sroRequiredIndicator.textContent = '(required)';
				labelTail.appendChild(sroRequiredIndicator);
				const requiredIndicator = document.createElement('div');
				requiredIndicator.setAttribute('aria-hidden', 'true');
				requiredIndicator.classList.add('required-indicator');
				requiredIndicator.textContent = '✱';
				labelTail.appendChild(requiredIndicator);
			}
		}
	}

	getResponse(): Array<SubResponseType> | null {
		return this.response;
	}

	hasResponse(): boolean {
		return this.response !== null && this.response.length > 0;
	}

	clearError(errorCode: SurveyResponseErrorCode): void {
		this.errorMap.delete(errorCode);
	}

	markError(error: SurveyResponseInputErrorType): void {
		this.errorMap.set(error.code, error.error);
	}

	allEntriesHaveResponse(): boolean {
		if (this.response === null) {
			return false;
		}

		const seenEntries = new Set(this.response.map((r: SubResponseType) => r.subEntryUuid));

		return seenEntries.size === this.rows.length;
	}

	selectEntry(subEntryUuid: string, subQuestionUuid: string): void {
		const existingSubResponseIndex = (this.response)? this.response.findIndex(
			(sr: SubResponseType) => sr.subEntryUuid === subEntryUuid && sr.subQuestionUuid === subQuestionUuid
		) : -1;

		if (!this.isMultiSelect) {
			if (this.response && existingSubResponseIndex > -1) {
				this.response.splice(existingSubResponseIndex, 1);
			} else {
				if (this.response === null) {
					this.response = [];
				}

				let otherForEntry = this.response.findIndex(
					(sr: SubResponseType) => sr.subEntryUuid === subEntryUuid
				);
				while (otherForEntry > -1) {
					this.response.splice(otherForEntry, 1);
					otherForEntry = this.response.findIndex(
						(sr: SubResponseType) => sr.subEntryUuid === subEntryUuid
					);
				}

				this.response.push({
					subEntryUuid,
					subQuestionUuid,
					selected: true
				});
			}
		} else {
			if (this.response && existingSubResponseIndex > -1) {
				this.response.splice(existingSubResponseIndex, 1);
			} else {
				if (this.response === null) {
					this.response = [];
				}

				this.response.push({
					subEntryUuid,
					subQuestionUuid,
					selected: true
				});
			}
		}

		if (this.allEntriesHaveResponse()) {
			this.clearError(SurveyResponseErrorCode.MISSING_REQUIRED);
		}

		if (this.responseGroup) {
			this.responseGroup.setResponse(
				this.question.uuid,
				null,
				null,
				null,
				(this.response && this.response.length)? [...this.response] : null
			);
		}
	}

	isSelected(subEntryUuid: string, subQuestionUuid: string): boolean {
		if (this.response === null) {
			return false;
		}

		return this.response.find(
			(sr: SubResponseType) => sr.subEntryUuid === subEntryUuid && sr.subQuestionUuid === subQuestionUuid && sr.selected
		) !== undefined;
	}

	focusedInfo(): { rowUuid: string, colUuid: string, index: number } | null {
		const table = this.$refs.table as HTMLTableElement | undefined;

		if (table === undefined) {
			return null;
		}

		if (document.activeElement && table.contains(document.activeElement)) {
			const closest = document.activeElement.closest('.mq-cell');
			if (
				closest === null
				|| closest.hasAttribute('data-row-uuid') === null
				|| closest.hasAttribute('data-col-uuid') === null
			) {
				return null;
			}

			const dataIndex = closest.getAttribute('data-index');
			return {
				rowUuid: closest.getAttribute('data-row-uuid')!,
				colUuid: closest.getAttribute('data-col-uuid')!,
				index: (dataIndex !== null)? parseInt(dataIndex) : -1
			};
		}

		return null;
	}

	focusRow(index: number): void {
		const subEntry = this.rows[index];
		if (subEntry === undefined) {
			return;
		}

		const existingRowSelections = (this.response)? this.response.filter(
			(sr: SubResponseType) => sr.subEntryUuid === subEntry.uuid
		) : null;

		const focusedInfo = this.focusedInfo();

		if (!this.isMultiSelect && existingRowSelections !== null && existingRowSelections.length > 0) {
			const useSubResponse = existingRowSelections[0];
			this.focusCell(useSubResponse.subEntryUuid, useSubResponse.subQuestionUuid);
			return;
		} else if (this.isMultiSelect && focusedInfo) {
			this.focusCell(this.rows[index].uuid, focusedInfo.colUuid);
			return;
		}

		this.focusCell(this.rows[index].uuid, this.columns[0].uuid);
	}

	moveFocus(direction: 'up' | 'down' | 'left' | 'right'): void {
		const focusedInfo = this.focusedInfo();

		if (focusedInfo === null) {
			this.focusRow(0);
			return;
		}

		const { rowUuid, colUuid, index } = focusedInfo;

		let newIndex = index;
		switch(direction) {
			case 'up':
				newIndex -= this.columns.length;
			break;
			case 'down':
				newIndex += this.columns.length;
			break;
			case 'left':
				newIndex -= 1;
			break;
			case 'right':
				newIndex += 1;
			break;
		}

		const originalRowIndex = Math.floor(index / this.columns.length);
		const newRowIndex = Math.floor(newIndex / this.columns.length);

		if (newRowIndex > originalRowIndex) {
			if (newRowIndex > this.rows.length - 1) {
				this.focusRow(0);
				return;
			}
			this.focusRow(newRowIndex);
			return;
		} else if (newRowIndex < originalRowIndex) {
			if (newRowIndex < 0) {
				this.focusRow(this.rows.length - 1);
				return;
			}
			this.focusRow(newRowIndex);
			return;
		} else { // Only shifted columns not rows
			const newColumnIndex = newIndex % this.columns.length;
			this.focusCell(rowUuid, this.columns[newColumnIndex].uuid);

			if (
				!this.isMultiSelect
				&& this.response !== null
				&& this.response.findIndex(
					(sr: SubResponseType) => sr.subEntryUuid === rowUuid
				) > -1
			) { // If there is already an answer, wcag says we should select on move
				this.selectEntry(rowUuid, this.columns[newColumnIndex].uuid);
			}
			return;
		}
	}

	focusCell(rowUuid: string, colUuid: string): void {
		const table = this.$refs.table as HTMLTableElement | undefined;

		if (table === undefined) {
			return;
		}

		const cell = table.querySelector(
			`div.mq-cell[data-col-uuid="${colUuid}"][data-row-uuid="${rowUuid}"]`
		) as HTMLDivElement | null;

		if (cell !== null) {
			(cell.querySelector('input[type="radio"]')! as HTMLInputElement).focus();
		}
	}

	get isMultiSelect(): boolean {
		if (this.question === null) {
			return false;
		}

		const isMultiSelect = this.question.options[QuestionOptionKey.MULTI_SELECT];

		if (isMultiSelect === undefined) {
			return false;
		}

		return !!isMultiSelect.intValue!;
	}

	get randomizeRows(): boolean {
		if (this.question === null) {
			return false;
		}

		const randomize = this.question.options[QuestionOptionKey.RANDOMIZE_ROWS];

		if (randomize === undefined) {
			return false;
		}

		return !!randomize.intValue!;
	}

	get randomizeColumns(): boolean {
		if (this.question === null) {
			return false;
		}

		const randomize = this.question.options[QuestionOptionKey.RANDOMIZE_COLUMNS];

		if (randomize === undefined) {
			return false;
		}

		return !!randomize.intValue!;
	}

	get firstColumnStyle(): {[key: string]: string} {
		return {
			"width": "250px",
			"minWidth": "200px"
		};
	}

	get subColumnStyle(): {[key: string]: string} {
		return {
			"width": "150px",
			"minWidth": "100px"
		};
	}

	get columns(): Array<PublishedSubQuestionType> {
		if (!this.randomizeColumns) {
			return this.question.subQuestions;
		}

		const toShuffle: Array<PublishedSubQuestionType> = [...this.question.subQuestions];
		inPlaceShuffleArray<PublishedSubQuestionType>(toShuffle);

		return toShuffle;
	}

	get rows(): Array<PublishedSubQuestionEntryType> {
		if (!this.randomizeRows) {
			return this.question.subEntries;
		}

		const toShuffle: Array<PublishedSubQuestionEntryType> = [...this.question.subEntries];
		inPlaceShuffleArray<PublishedSubQuestionEntryType>(toShuffle);

		return toShuffle;
	}

	get errorMessages(): Array<string> {
		return Array.from(this.errorMap.values());
	}

	get responseGroup(): ResponseGroup | null {
		return this.responseStore.responseGroup;
	}

	get responseStore() {
		return useResponseStore();
	}
}
</script>

<style lang="scss">
.hdl-matrix {
	@apply relative;

	&.has-error {
		label {
			@apply text-hdl-wc-red;
		}
	}

	.errors {
		@apply text-hdl-wc-red text-sm;

		ul {
			list-style: none;
			margin: 0;
			padding: 0;

			li {
				margin: 0;
				list-style-type: none;
			}
		}
	}

	.matrix-container {
		max-width: var(--survey-width);

		table {
			border-collapse: separate;
			border-spacing: 0;
		}

		thead {
			tr {
				background: var(--srv-hover-fill);

				th {
					@apply font-normal p-1 pt-2;

					&:not(:first-child) {
						@apply border-l;
						border-color: var(--srv-background);
					}

					textarea {
						text-align: center;
					}
				}
			}
		}

		tbody {
			tr {
				td {
					@apply p-1;

					&:not(:first-child) {
						@apply border-l;
						border-color: var(--srv-background);
					}
				}

				&:hover,
				&:focus-within {
					background: var(--srv-hover-fill);
				}

				td > .mq-cell {
					@apply flex flex-row items-center justify-center;

					&:focus {
						.checkbox-indicator,
						.radio-indicator {
							border: 1px solid white;
							box-shadow: 0 0 0 1px black;
							outline: none;
						}
						outline: none;
					}
				}
			}
		}
	}
}
</style>
